import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarGridItem } from '../../hooks/useCalendarData';
import type { CalendarWorkout } from '../../services/api';
import type { CalendarFileDropResult } from '../../utils/calendarFileDrop';
import { getWeekStripDays } from '../../utils/calendarPickerLocale';
import { useLocale } from '../../hooks/useLocale';
import { jalaliDateKey, jalaliToDate, type JalaliDayRef } from '../../utils/jalali';
import { CalendarDayRow } from './CalendarDayRow';
import './calendar-week-timeline.css';

function emptyDayItem(d: JalaliDayRef): CalendarGridItem {
  return {
    year: d.year,
    month: d.month,
    day: d.day,
    dayOfWeek: jalaliToDate(d.year, d.month, d.day).getDay(),
    extra: {
      day: '',
      workouts: [],
      events: [],
      notes: [],
      userMetrics: [],
      today: null,
    },
  };
}

interface Props {
  weekAnchor: { year: number; month: number; day: number };
  selectedDateKey: string;
  dayByKey: Map<string, CalendarGridItem>;
  onWorkoutPress?: (workout: CalendarWorkout) => void;
  userId?: number | null;
  onReload?: () => void | Promise<void>;
  onUploadComplete?: (result: CalendarFileDropResult) => void | Promise<void>;
  onMetricMoved?: (targetDateIso: string) => void | Promise<void>;
}

function weekStartKey(
  anchor: { year: number; month: number; day: number },
  locale: string,
): string {
  const [first] = getWeekStripDays(anchor.year, anchor.month, anchor.day, locale);
  return first.dateKey;
}

function buildWeekEntries(
  weekAnchor: { year: number; month: number; day: number },
  dayByKey: Map<string, CalendarGridItem>,
  locale: string,
): { dateKey: string; item: CalendarGridItem }[] {
  const weekDays = getWeekStripDays(weekAnchor.year, weekAnchor.month, weekAnchor.day, locale);

  return weekDays.map((d) => {
    const item = dayByKey.get(d.dateKey) ?? emptyDayItem(d);
    return { dateKey: d.dateKey, item };
  });
}

/** Weekly mode — all 7 days with + add row on empty days; highlight and scroll to the selected day. */
export function CalendarWeekDetail({
  weekAnchor,
  selectedDateKey,
  dayByKey,
  onWorkoutPress,
  userId,
  onReload,
  onUploadComplete,
  onMetricMoved,
}: Props) {
  const { locale } = useLocale();
  const weekKey = useMemo(() => weekStartKey(weekAnchor, locale), [weekAnchor, locale]);
  const weekEntries = useMemo(
    () => buildWeekEntries(weekAnchor, dayByKey, locale),
    [weekAnchor, dayByKey, locale],
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const dayBlockRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const prevWeekKeyRef = useRef(weekKey);
  const [enterGeneration, setEnterGeneration] = useState(1);

  useEffect(() => {
    if (prevWeekKeyRef.current === weekKey) return;
    prevWeekKeyRef.current = weekKey;
    setEnterGeneration((g) => g + 1);
  }, [weekKey]);

  useEffect(() => {
    const el = dayBlockRefs.current.get(selectedDateKey);
    if (!el) return;
    const timer = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [selectedDateKey, weekKey, weekEntries.length, enterGeneration]);

  return (
    <div
      key={weekKey}
      ref={panelRef}
      className="cal-week-detail cal-week-detail--animated cal-week-detail--timeline"
      data-enter-gen={enterGeneration}
    >
      <div className="cal-week-detail__days">
        {weekEntries.map(({ dateKey, item }, index) => {
          const focused = dateKey === selectedDateKey;
          return (
            <div
              key={`${weekKey}-${jalaliDateKey(item.year, item.month, Number(item.day))}`}
              ref={(node) => {
                if (node) dayBlockRefs.current.set(dateKey, node);
                else dayBlockRefs.current.delete(dateKey);
              }}
              className={[
                'cal-week-detail__day-block',
                focused ? 'cal-week-detail__day-block--focused' : '',
                enterGeneration > 0 ? 'cal-week-detail__day-block--enter' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                enterGeneration > 0
                  ? { animationDelay: `${Math.min(index, 7) * 52}ms` }
                  : undefined
              }
            >
              <CalendarDayRow
                item={item}
                userId={userId}
                onWorkoutPress={onWorkoutPress}
                onReload={onReload}
                onUploadComplete={onUploadComplete}
                onMetricMoved={onMetricMoved}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
